import { useState, useEffect, useRef, useCallback } from 'react';
import { db, storage } from '../../services/firebase';
import {
  collection, query, where, orderBy, onSnapshot, addDoc,
  serverTimestamp, doc, setDoc, deleteDoc, getDoc,
  updateDoc, increment, limit, startAfter, getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { StoriesBar } from './StoriesBar';
import {
  Heart, MessageCircle, Send, Image, Video, X, Globe, Users,
  MoreHorizontal, Trash2, ChevronDown, ChevronLeft,
} from 'lucide-react';

interface Post {
  id: string;
  uid: string;
  displayName: string;
  photoURL?: string;
  text: string;
  mediaURL?: string;
  mediaType?: 'image' | 'video';
  visibility: 'public' | 'contacts';
  createdAt: any;
  likesCount: number;
  commentsCount: number;
  likedByMe?: boolean;
}

interface Comment {
  id: string;
  uid: string;
  displayName: string;
  photoURL?: string;
  text: string;
  createdAt: any;
}

interface Props {
  currentUser: any;
  friendUids: string[];
  onClose?: () => void;
}

const PAGE_SIZE = 10;

export const Board = ({ currentUser, friendUids, onClose }: Props) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);

  // Composer
  const [composerText, setComposerText] = useState('');
  const [composerMedia, setComposerMedia] = useState<File | null>(null);
  const [composerPreview, setComposerPreview] = useState<string | null>(null);
  const [composerMediaType, setComposerMediaType] = useState<'image' | 'video'>('image');
  const [composerVisibility, setComposerVisibility] = useState<'public' | 'contacts'>('public');
  const [posting, setPosting] = useState(false);

  // Comments
  const [openComments, setOpenComments] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  // Post menu
  const [postMenu, setPostMenu] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const getInitials = (name: string) => name?.slice(0, 2).toUpperCase() || 'U';

  // ── Load posts ──
  const loadPosts = useCallback(async (after?: any) => {
    const contactsAndMe = [currentUser.uid, ...friendUids];

    // Public posts from everyone + contacts-only posts from friends/me
    // We fetch both and merge, dedup by id
    const publicQ = query(
      collection(db, 'posts'),
      where('visibility', '==', 'public'),
      orderBy('createdAt', 'desc'),
      ...(after ? [startAfter(after)] : []),
      limit(PAGE_SIZE),
    );

    const contactsQ = query(
      collection(db, 'posts'),
      where('uid', 'in', contactsAndMe.slice(0, 30)),
      where('visibility', '==', 'contacts'),
      orderBy('createdAt', 'desc'),
      ...(after ? [startAfter(after)] : []),
      limit(PAGE_SIZE),
    );

    const [pubSnap, conSnap] = await Promise.all([
      getDocs(publicQ),
      getDocs(contactsQ),
    ]);

    const seen = new Set<string>();
    const merged: Post[] = [];

    for (const snap of [pubSnap, conSnap]) {
      for (const d of snap.docs) {
        if (seen.has(d.id)) continue;
        seen.add(d.id);
        const data = d.data();
        merged.push({
          id: d.id,
          uid: data.uid,
          displayName: data.displayName || 'Usuario',
          photoURL: data.photoURL,
          text: data.text || '',
          mediaURL: data.mediaURL,
          mediaType: data.mediaType,
          visibility: data.visibility,
          createdAt: data.createdAt,
          likesCount: data.likesCount || 0,
          commentsCount: data.commentsCount || 0,
          likedByMe: false, // se actualiza abajo en batch
        });
      }
    }

    // Batch check likes — en paralelo, no en secuencia
    await Promise.all(merged.map(async (post) => {
      try {
        const likeSnap = await getDoc(doc(db, 'posts', post.id, 'likes', currentUser.uid));
        post.likedByMe = likeSnap.exists();
      } catch { /* sin permisos = no liked */ }
    }));

    // Sort by date desc
    merged.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

    const newLast = pubSnap.docs[pubSnap.docs.length - 1] || null;
    setLastDoc(newLast);
    setHasMore(pubSnap.docs.length === PAGE_SIZE);
    return merged;
  }, [currentUser.uid, friendUids.join(',')]);

  useEffect(() => {
    setLoading(true);
    loadPosts().then(p => { setPosts(p); setLoading(false); });
  }, [loadPosts]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const more = await loadPosts(lastDoc);
    setPosts(prev => {
      const ids = new Set(prev.map(p => p.id));
      return [...prev, ...more.filter(p => !ids.has(p.id))];
    });
    setLoadingMore(false);
  };

  // ── Post submit ──
  const handlePost = async () => {
    if (!composerText.trim() && !composerMedia) return;
    setPosting(true);
    try {
      let mediaURL: string | undefined;
      let mediaType: 'image' | 'video' | undefined;

      if (composerMedia) {
        const path = `posts/${currentUser.uid}/${Date.now()}_${composerMedia.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, composerMedia);
        mediaURL = await getDownloadURL(storageRef);
        mediaType = composerMediaType;
      }

      await addDoc(collection(db, 'posts'), {
        uid: currentUser.uid,
        displayName: currentUser.displayName || 'Usuario',
        photoURL: currentUser.photoURL || null,
        text: composerText.trim(),
        ...(mediaURL && { mediaURL, mediaType }),
        visibility: composerVisibility,
        createdAt: serverTimestamp(),
        likesCount: 0,
        commentsCount: 0,
      });

      setComposerText('');
      setComposerMedia(null);
      setComposerPreview(null);

      // Refresh feed
      const fresh = await loadPosts();
      setPosts(fresh);
    } catch (e) { console.error(e); }
    finally { setPosting(false); }
  };

  // ── Like toggle ──
  const handleLike = async (post: Post) => {
    const likeRef = doc(db, 'posts', post.id, 'likes', currentUser.uid);
    const postRef = doc(db, 'posts', post.id);

    // Optimistic update
    setPosts(prev => prev.map(p =>
      p.id === post.id
        ? { ...p, likedByMe: !p.likedByMe, likesCount: p.likesCount + (p.likedByMe ? -1 : 1) }
        : p
    ));

    if (post.likedByMe) {
      await deleteDoc(likeRef);
      await updateDoc(postRef, { likesCount: increment(-1) });
    } else {
      await setDoc(likeRef, { uid: currentUser.uid, createdAt: serverTimestamp() });
      await updateDoc(postRef, { likesCount: increment(1) });
    }
  };

  // ── Comments ──
  const openCommentsFor = (postId: string) => {
    setOpenComments(postId);
    if (comments[postId]) return;

    const q = query(
      collection(db, 'posts', postId, 'comments'),
      orderBy('createdAt', 'asc'),
    );
    onSnapshot(q, snap => {
      setComments(prev => ({
        ...prev,
        [postId]: snap.docs.map(d => ({ id: d.id, ...d.data() } as Comment)),
      }));
    });
  };

  const handleComment = async (postId: string) => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      await addDoc(collection(db, 'posts', postId, 'comments'), {
        uid: currentUser.uid,
        displayName: currentUser.displayName || 'Usuario',
        photoURL: currentUser.photoURL || null,
        text: commentText.trim(),
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'posts', postId), { commentsCount: increment(1) });
      setCommentText('');
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, commentsCount: p.commentsCount + 1 } : p));
    } catch (e) { console.error(e); }
    finally { setPostingComment(false); }
  };

  // ── Delete post ──
  const handleDeletePost = async (postId: string) => {
    await deleteDoc(doc(db, 'posts', postId));
    setPosts(prev => prev.filter(p => p.id !== postId));
    setPostMenu(null);
  };

  // ── Media selection ──
  const handleMediaSelect = (file: File, type: 'image' | 'video') => {
    setComposerMedia(file);
    setComposerMediaType(type);
    const reader = new FileReader();
    reader.onload = e => setComposerPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const formatTime = (ts: any) => {
    if (!ts?.toDate) return '';
    const d = ts.toDate();
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return 'Ahora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="board">

      {/* ── Header móvil ── */}
      {onClose && (
        <div className="board-mobile-header">
          <button className="board-mobile-header__back" onClick={onClose}>
            <ChevronLeft size={22} />
          </button>
          <span className="board-mobile-header__title">Board</span>
        </div>
      )}

      {/* ── Stories Bar ── */}
      <StoriesBar currentUser={currentUser} friendUids={friendUids} />

      {/* ── Post Composer ── */}
      <div className="board-composer">
        <div className="board-composer__av">
          {currentUser.photoURL
            ? <img src={currentUser.photoURL} alt="" />
            : <span>{getInitials(currentUser.displayName || 'U')}</span>}
        </div>
        <div className="board-composer__body">
          <textarea
            ref={textareaRef}
            className="board-composer__input"
            placeholder="¿Qué está pasando?"
            value={composerText}
            onChange={e => setComposerText(e.target.value)}
            rows={composerText.length > 60 ? 3 : 1}
            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handlePost(); }}
          />

          {/* Media preview */}
          {composerPreview && (
            <div className="board-composer__preview">
              {composerMediaType === 'video'
                ? <video src={composerPreview} controls className="board-composer__preview-media" />
                : <img src={composerPreview} alt="" className="board-composer__preview-media" />}
              <button className="board-composer__remove-media" onClick={() => { setComposerMedia(null); setComposerPreview(null); }}>
                <X size={14} />
              </button>
            </div>
          )}

          <div className="board-composer__footer">
            <div className="board-composer__actions">
              <button className="board-composer__action-btn" onClick={() => fileInputRef.current?.click()} title="Imagen">
                <Image size={18} />
              </button>
              <button className="board-composer__action-btn" onClick={() => videoInputRef.current?.click()} title="Video">
                <Video size={18} />
              </button>
              <button
                className={`board-composer__visibility ${composerVisibility === 'contacts' ? 'board-composer__visibility--contacts' : ''}`}
                onClick={() => setComposerVisibility(v => v === 'public' ? 'contacts' : 'public')}
                title={composerVisibility === 'public' ? 'Público' : 'Solo contactos'}
              >
                {composerVisibility === 'public' ? <Globe size={15} /> : <Users size={15} />}
                {composerVisibility === 'public' ? 'Todos' : 'Contactos'}
              </button>
            </div>
            <button
              className="board-composer__submit"
              disabled={posting || (!composerText.trim() && !composerMedia)}
              onClick={handlePost}
            >
              {posting ? '...' : 'Publicar'}
            </button>
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleMediaSelect(e.target.files[0], 'image'); e.target.value = ''; }} />
        <input ref={videoInputRef} type="file" accept="video/*" style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleMediaSelect(e.target.files[0], 'video'); e.target.value = ''; }} />
      </div>

      {/* ── Feed ── */}
      <div className="board-feed">
        {loading && (
          <>
            {[1, 2, 3].map(i => (
              <div key={i} className="post-card post-card--skeleton">
                <div className="skeleton post-card__sk-av" />
                <div className="post-card__sk-body">
                  <div className="skeleton post-card__sk-name" />
                  <div className="skeleton post-card__sk-text" />
                  <div className="skeleton post-card__sk-text post-card__sk-text--short" />
                </div>
              </div>
            ))}
          </>
        )}

        {!loading && posts.length === 0 && (
          <div className="board-empty">
            <div className="board-empty__icon">📢</div>
            <p>Aún no hay publicaciones.</p>
            <p>¡Sé el primero en publicar algo!</p>
          </div>
        )}

        {posts.map(post => (
          <div key={post.id} className="post-card">
            {/* Header */}
            <div className="post-card__header">
              <div className="post-card__av">
                {post.photoURL
                  ? <img src={post.photoURL} alt="" />
                  : <span>{getInitials(post.displayName)}</span>}
              </div>
              <div className="post-card__meta">
                <span className="post-card__name">{post.displayName}</span>
                <div className="post-card__sub">
                  <span className="post-card__time">{formatTime(post.createdAt)}</span>
                  <span className="post-card__visibility">
                    {post.visibility === 'public' ? <Globe size={11} /> : <Users size={11} />}
                  </span>
                </div>
              </div>
              {post.uid === currentUser.uid && (
                <div style={{ position: 'relative', marginLeft: 'auto' }}>
                  <button className="post-card__menu-btn" onClick={() => setPostMenu(postMenu === post.id ? null : post.id)}>
                    <MoreHorizontal size={18} />
                  </button>
                  {postMenu === post.id && (
                    <div className="post-card__menu">
                      <button className="post-card__menu-item post-card__menu-item--danger" onClick={() => handleDeletePost(post.id)}>
                        <Trash2 size={14} /> Eliminar
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Text */}
            {post.text && <p className="post-card__text">{post.text}</p>}

            {/* Media */}
            {post.mediaURL && (
              <div className="post-card__media">
                {post.mediaType === 'video'
                  ? <video src={post.mediaURL} controls className="post-card__media-el" />
                  : <img src={post.mediaURL} alt="" className="post-card__media-el" />}
              </div>
            )}

            {/* Actions */}
            <div className="post-card__actions">
              <button
                className={`post-card__action ${post.likedByMe ? 'post-card__action--liked' : ''}`}
                onClick={() => handleLike(post)}
              >
                <Heart size={17} fill={post.likedByMe ? 'currentColor' : 'none'} />
                {post.likesCount > 0 && <span>{post.likesCount}</span>}
              </button>
              <button
                className="post-card__action"
                onClick={() => openComments === post.id ? setOpenComments(null) : openCommentsFor(post.id)}
              >
                <MessageCircle size={17} />
                {post.commentsCount > 0 && <span>{post.commentsCount}</span>}
              </button>
            </div>

            {/* Comments section */}
            {openComments === post.id && (
              <div className="post-card__comments">
                {(comments[post.id] || []).map(c => (
                  <div key={c.id} className="comment">
                    <div className="comment__av">
                      {c.photoURL
                        ? <img src={c.photoURL} alt="" />
                        : <span>{getInitials(c.displayName)}</span>}
                    </div>
                    <div className="comment__bubble">
                      <span className="comment__name">{c.displayName}</span>
                      <span className="comment__text">{c.text}</span>
                    </div>
                  </div>
                ))}
                <div className="comment-input">
                  <div className="comment__av comment__av--sm">
                    {currentUser.photoURL
                      ? <img src={currentUser.photoURL} alt="" />
                      : <span>{getInitials(currentUser.displayName || 'U')}</span>}
                  </div>
                  <input
                    className="comment-input__field"
                    placeholder="Escribe un comentario..."
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleComment(post.id); }}
                  />
                  <button
                    className="comment-input__send"
                    disabled={postingComment || !commentText.trim()}
                    onClick={() => handleComment(post.id)}
                  >
                    <Send size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Load more */}
        {hasMore && !loading && (
          <button className="board-load-more" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? '...' : <><ChevronDown size={16} /> Ver más</>}
          </button>
        )}
      </div>
    </div>
  );
};